<template>
  <TabsRoot v-if="tabs.length > 1" v-model="activeTab" class="SearchTripTabs">
    <TabsList ref="tabList" class="SearchTripTabs-list">
      <TabsTrigger
        v-for="tab of tabs"
        :key="tab.id"
        :value="tab.id"
        class="SearchTripTabs-tab"
      >
        {{ tab.label }}
      </TabsTrigger>
    </TabsList>

    <!-- One panel shared by every tab: the slot is re-driven by the model, never unmounted. -->
    <TabsContent :value="activeTab" forceMount class="SearchTripTabs-panel">
      <slot />
    </TabsContent>
  </TabsRoot>

  <slot v-else />
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { useTemplateRef, watch } from 'vue';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import type { SearchTripTabsEmits, SearchTripTabsProps } from './SearchTripTabs.model';

defineProps<SearchTripTabsProps>();

const emit = defineEmits<SearchTripTabsEmits>();

const activeTab = defineModel<string>({ required: true });

// Content box: the list's bottom padding overlaps the card, so it takes no layout space.
const tabList = useTemplateRef('tabList');
const { height: tabListHeight } = useElementSize(tabList, undefined, { box: 'content-box' });
watch(tabListHeight, height => emit('tabsHeight', height));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .SearchTripTabs {
    --overlap: #{vars.$molecules-searchtrip-box-border-stroke};

    &-list {
      position: relative;
      z-index: 1;
      display: flex;
      gap: vars.$dimensions-d-1;
      overflow-x: auto;
      scrollbar-width: none;
      // A row over the card's top border, inside the scroll box so the active tab can reach it unclipped.
      padding-block-end: var(--overlap);
      margin-block-end: calc(-1 * var(--overlap));
    }

    &-tab {
      flex-shrink: 0;
      padding: vars.$atoms-tab-padding-top-bottom vars.$atoms-tab-padding-left-right;
      background-color: vars.$atoms-tab-default-bg-color;
      border: vars.$atoms-tab-border-stroke solid vars.$atoms-tab-default-border-color;
      border-block-end: none;
      border-start-start-radius: vars.$molecules-searchtrip-box-lg-md-border-radius;
      border-start-end-radius: vars.$molecules-searchtrip-box-lg-md-border-radius;
      color: vars.$atoms-tab-default-text-color;
      white-space: nowrap;
      cursor: pointer;
      @include text.text(sm, vars.$font-weight-text-600);

      // Drawn inside the tab: the scrolling list would clip an outer ring.
      &:focus-visible {
        outline: vars.$dimensions-d-0-5 solid vars.$color-brand-focus;
        outline-offset: calc(-1 * #{vars.$dimensions-d-1});
      }

      // Covers the card's top border so the active tab and the card read as one surface.
      &[data-state='active'] {
        margin-block-end: calc(-1 * var(--overlap));
        padding-block-end: calc(#{vars.$atoms-tab-padding-top-bottom} + var(--overlap));
        background-color: vars.$molecules-searchtrip-box-bg-color;
        border-color: vars.$molecules-searchtrip-box-border-color;
        color: vars.$molecules-searchtrip-box-disclaimer-color;
      }
    }

    &-panel .SearchTrip {
      border-start-start-radius: 0;
    }
  }
}
</style>
